/**
 * Canonical display time zone for donation-related surfaces.
 *
 * All storage remains UTC; every donor-facing surface (admin dashboard,
 * ledger, donor history, statement listings, exports) formats through
 * these helpers so the *same* calendar date is shown everywhere for a
 * given transaction — regardless of the admin's browser location.
 *
 * Configurable via REACT_APP_TIMEZONE_DISPLAY (default: America/New_York).
 */

export const DISPLAY_TZ =
  process.env.REACT_APP_TIMEZONE_DISPLAY || 'America/New_York';

/** Short zone abbreviation (EST / EDT), DST-aware. */
export function tzAbbrev(date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: DISPLAY_TZ,
      timeZoneName: 'short',
    }).formatToParts(date);
    return parts.find((p) => p.type === 'timeZoneName')?.value || 'ET';
  } catch {
    return 'ET';
  }
}

/** Format an ISO / date-like value as a short date in the display zone. */
export function formatDate(v, opts = {}) {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: DISPLAY_TZ,
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    ...opts,
  }).format(d);
}

/** Format an ISO / date-like value as a full timestamp + zone label. */
export function formatDateTime(v) {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  const s = new Intl.DateTimeFormat('en-US', {
    timeZone: DISPLAY_TZ,
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
  return `${s} ${tzAbbrev(d)}`;
}

/** Return YYYY-MM-DD in the display zone — useful for date filters. */
export function isoLocalDate(v) {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPLAY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const g = (t) => parts.find((p) => p.type === t)?.value || '';
  return `${g('year')}-${g('month')}-${g('day')}`;
}
